'use client';

import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Check, Loader2, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { resolveOwnedPages } from '@/features/ocr';
import { useSubmitContainerVerdictMutation } from '../services/ocr-qa.mutations';
import {
  OcrQaPageNotesPicker,
  type RejectablePage
} from './ocr-qa-page-notes-picker';
import type { OcrQaResult, OcrQaTier, OcrQaVerdict } from '../types';

interface Props {
  tier: OcrQaTier;
  containerId: number;
  verdict: OcrQaVerdict | null;
  /** Pages the verdict applies to — drives the per-page note inputs. */
  actionableResults: OcrQaResult[];
  onClose: () => void;
  onSuccess?: () => void;
}

interface ActionablePage extends RejectablePage {
  resultId: number;
  /** Needed to read this page's own standing verdict off the result. */
  result: OcrQaResult;
}

/**
 * Container-level verdict confirmation. Accept/reject applies to every
 * actionable page as one unit — a reject returns the whole container one tier
 * back, which is why the page picker below is documentation rather than scope.
 *
 * On reject the reviewer ticks the pages that are actually wrong and explains
 * each. It used to demand a reason for EVERY page, which meant twelve write-ups
 * to report one bad scan, and left the operator no signal about which pages
 * were fine. The pages left untouched are the signal: they carry no note, and
 * the OCR screen's page filter reads them back as "not yet reviewed" while
 * pages of results this tier accepted read as accepted.
 */
export function OcrQaVerdictDialog({
  tier,
  containerId,
  verdict,
  actionableResults,
  onClose,
  onSuccess
}: Props) {
  const t = useTranslations('ocrQa');
  const [notes, setNotes] = useState('');
  const [pageNotes, setPageNotes] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const mutation = useSubmitContainerVerdictMutation();

  const actionablePages: ActionablePage[] = useMemo(
    () =>
      actionableResults.flatMap((result) =>
        // Only the pages this result actually covers. `result.pages` is the
        // whole container's list on every result, so enumerating it produced a
        // note slot for pages the result owns nothing on — and a note filed
        // there keys into a slot the page filter never reads.
        resolveOwnedPages(result).map((page) => ({
          ...page,
          resultId: result.id,
          result,
          key: `${result.id}:${page.pageNumber}`
        }))
      ),
    [actionableResults]
  );

  const isOpen = verdict !== null;
  const isReject = verdict === 'rejected';

  // Pages the reviewer already faulted one at a time arrive pre-ticked, carrying
  // the reason they already wrote. Making them type it again to reject the
  // container is making them repeat themselves — and an empty box beside a page
  // that visibly has a reason reads as though the reason had been lost.
  const alreadyRejected = useMemo(() => {
    const seeded: Record<string, string> = {};
    for (const page of actionablePages) {
      const own = (page.result.qa_page_reviews ?? []).find(
        (r) =>
          r.page_number === page.pageNumber &&
          r.tier === tier &&
          r.verdict === 'rejected' &&
          !r.superseded_at
      );
      if (own) seeded[page.key] = own.notes ?? '';
    }
    return seeded;
  }, [actionablePages, tier]);

  // Pages the reviewer already accepted are settled and are NOT candidates for
  // "which pages have a problem" — offering them back asks the reviewer to
  // reconsider a call they already made, in a dialog about a different subject.
  // Changing that verdict is done on the page itself ("reject instead").
  const acceptedPages = useMemo(
    () =>
      actionablePages.filter((page) =>
        (page.result.qa_page_reviews ?? []).some(
          (r) =>
            r.page_number === page.pageNumber &&
            r.tier === tier &&
            r.verdict === 'accepted' &&
            !r.superseded_at
        )
      ),
    [actionablePages, tier]
  );
  const acceptedKeys = new Set(acceptedPages.map((p) => p.key));
  const selectablePages = actionablePages.filter(
    (p) => !acceptedKeys.has(p.key)
  );

  useEffect(() => {
    if (!isOpen) return;
    // Seeded on open, not on mount: the dialog lives for the whole page, and the
    // page verdicts can change underneath it between openings. Anything the
    // reviewer has already typed in this session wins.
    setPageNotes((prev) => ({ ...alreadyRejected, ...prev }));
    setSelected((prev) => new Set([...prev, ...Object.keys(alreadyRejected)]));
  }, [isOpen, alreadyRejected]);
  // One page total means no choice to make — ticking a lone checkbox to unlock
  // the reason field is friction, not a decision.
  const effectiveSelected =
    selectablePages.length === 1 ? new Set([selectablePages[0].key]) : selected;
  const selectedPages = selectablePages.filter((p) =>
    effectiveSelected.has(p.key)
  );
  const missingNotes = selectedPages.filter(
    (p) => (pageNotes[p.key] ?? '').trim() === ''
  );
  // With every page already accepted there is nothing left to name, but the
  // container can still be wrong as a whole — the general note carries it.
  const canSubmit =
    !isReject ||
    selectablePages.length === 0 ||
    (selectedPages.length > 0 && missingNotes.length === 0);

  function reset() {
    setNotes('');
    setPageNotes({});
    setSelected(new Set());
  }

  function handleSubmit() {
    if (!verdict || !canSubmit) return;
    const nestedPageNotes: Record<number, Record<number, string>> = {};
    if (isReject) {
      for (const page of selectedPages) {
        nestedPageNotes[page.resultId] ??= {};
        nestedPageNotes[page.resultId][page.pageNumber] = (
          pageNotes[page.key] ?? ''
        ).trim();
      }
    }
    mutation.mutate(
      {
        tier,
        containerId,
        verdict,
        notes: notes.trim() || null,
        pageNotes: isReject ? nestedPageNotes : undefined
      },
      {
        onSuccess: () => {
          reset();
          onClose();
          onSuccess?.();
        }
      }
    );
  }

  function handleOpenChange(next: boolean) {
    if (!next && !mutation.isPending) {
      reset();
      onClose();
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className='max-h-[85svh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            {isReject ? (
              <X className='text-red-600' />
            ) : (
              <Check className='text-green-600' />
            )}
            {isReject
              ? t('confirmRejectContainerTitle')
              : t('confirmAcceptContainerTitle')}
          </DialogTitle>
          <DialogDescription>
            {isReject
              ? t('confirmRejectContainerDescription')
              : t('confirmAcceptContainerDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='ocr-qa-notes' className='text-sm'>
              {t('notesLabel')}
            </Label>
            <Textarea
              id='ocr-qa-notes'
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('notesPlaceholder')}
              rows={3}
              disabled={mutation.isPending}
            />
          </div>

          {isReject && selectablePages.length > 0 && (
            <>
              <OcrQaPageNotesPicker
                pages={selectablePages}
                selected={effectiveSelected}
                notes={pageNotes}
                disabled={mutation.isPending}
                onToggle={(key, next) =>
                  setSelected((prev) => {
                    const copy = new Set(prev);
                    if (next) copy.add(key);
                    else copy.delete(key);
                    return copy;
                  })
                }
                onNoteChange={(key, value) =>
                  setPageNotes((prev) => ({ ...prev, [key]: value }))
                }
              />
              {selectablePages.length > 1 && selectedPages.length === 0 && (
                <p className='text-xs text-red-600 dark:text-red-400'>
                  {t('pageSelectionRequiredHint')}
                </p>
              )}
              {acceptedPages.length > 0 && (
                <p className='text-muted-foreground text-xs'>
                  {t('acceptedPagesNotListed', { count: acceptedPages.length })}
                </p>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => handleOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('cancel')}
          </Button>
          <Button
            variant={isReject ? 'destructive' : 'default'}
            onClick={handleSubmit}
            disabled={mutation.isPending || !canSubmit}
          >
            {mutation.isPending && (
              <Loader2 className='me-2 h-4 w-4 animate-spin' />
            )}
            {isReject ? t('rejectContainer') : t('acceptContainer')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
